import api from './axios';

const BASE_URL = '/formularios';

// Types
export type FormularioTipo =
  | 'general'
  | 'profesor_alumnos'
  | 'manager_departamento'
  | 'evaluacion_managers';

export interface Formulario {
  id: number;
  titulo: string;
  descripcion?: string;
  estado: 'borrador' | 'activo' | 'desactivado' | 'cerrado' | 'completado' | 'eliminado';
  tipoFormulario?: FormularioTipo;
  respuestasAnonimas?: boolean;
  fechaDesde?: string;
  fechaHasta?: string;
  departamentosSeleccionados?: string[];
  departamento?: string | null;
  servicioId?: number;
  fechaLimite?: string;
  fechaEnvioProgramada?: string;
  enviadoAt?: string;
  creadoPor: number;
  createdAt: string;
  updatedAt: string;
  updatedBy?: number;
  esPlantilla: boolean;
  esEvaluacion?: boolean;
  rolesPermitidos?: string[];
  puntuacionMaxima?: number;
  notaMinima?: number;
  numeroPreguntasMostrar?: number;
  tiempoLimite?: number;
  preguntas?: FormularioPregunta[];
  competenciasPesos?: CompetenciaPesoDto[];
  creadoPorUsuario?: {
    id: number;
    nombreCompleto: string;
  };
}

export interface FormularioPregunta {
  id: number;
  formularioId: number;
  orden: number;
  tipo:
    | 'puntuacion'
    | 'texto_corto'
    | 'texto_largo'
    | 'seleccion_unica'
    | 'seleccion_multiple'
    | 'fecha'
    | 'hora'
    | 'test_seleccion_unica'
    | 'test_seleccion_multiple';
  pregunta: string;
  requerida: boolean;
  escalaMin?: number;
  escalaMax?: number;
  limiteCaracteres?: number;
  condicionesJson?: any;
  puntosManuales?: number;
  obligatoriaEnPool?: boolean;
  competencias?: Competencia[];
  competenciasIds?: number[];
  opciones?: FormularioOpcion[];
}

export interface FormularioOpcion {
  id: number;
  preguntaId: number;
  texto: string;
  orden: number;
  esCorrecta?: boolean;
}

export interface CreateFormularioDto {
  titulo: string;
  descripcion?: string;
  estado?: 'borrador' | 'activo' | 'desactivado' | 'cerrado' | 'completado';
  tipoFormulario?: FormularioTipo;
  respuestasAnonimas?: boolean;
  fechaDesde?: string;
  fechaHasta?: string;
  departamentosSeleccionados?: string[];
  departamento?: string | null;
  fechaLimite?: string;
  fechaEnvioProgramada?: string;
  esPlantilla?: boolean;
  esEvaluacion?: boolean;
  rolesPermitidos?: string[];
  tiempoLimite?: number;
  puntuacionMaxima?: number;
  notaMinima?: number;
  numeroPreguntasMostrar?: number;
  preguntas?: CreatePreguntaDto[];
  servicioId?: number;
  competenciasPesos?: CompetenciaPesoDto[];
}

export interface CreatePreguntaDto {
  orden: number;
  tipo:
    | 'puntuacion'
    | 'texto_corto'
    | 'texto_largo'
    | 'seleccion_unica'
    | 'seleccion_multiple'
    | 'fecha'
    | 'hora'
    | 'test_seleccion_unica'
    | 'test_seleccion_multiple';
  pregunta: string;
  requerida?: boolean;
  escalaMin?: number;
  escalaMax?: number;
  limiteCaracteres?: number;
  condicionesJson?: any;
  puntosManuales?: number;
  obligatoriaEnPool?: boolean;
  competenciasIds?: number[];
  opciones?: CreateOpcionDto[];
}

export interface Competencia {
  id: number;
  nombre: string;
  descripcion?: string;
  activa: boolean;
}

export interface CompetenciaPesoDto {
  competenciaId: number;
  porcentaje: number;
}

export interface CreateOpcionDto {
  texto: string;
  orden: number;
  esCorrecta?: boolean;
}

export interface IniciarEvaluacionResponse {
  tiempoLimite: number;
  fechaInicio: string;
}

export interface VerificarTiempoResponse {
  expirada: boolean;
  tiempoRestante?: number;
  tiempoTranscurrido?: number;
}

export interface PreguntasEvaluacionResponse {
  intentoNumero: number;
  preguntas: FormularioPregunta[];
  preguntasMostradas: number[];
}

export type UpdateFormularioDto = Partial<CreateFormularioDto>;

export interface AsignarFormularioDto {
  roles?: string[];
  usuarioIds?: number[];
  desasignarUsuarioIds?: number[];
}

export interface FilterFormularioDto {
  search?: string;
  estado?: string;
  page?: number;
  limit?: number;
  esEvaluacion?: boolean;
  departamento?: string;
}

export interface FormularioPendiente {
  id: number;
  titulo: string;
  descripcion?: string;
  fechaLimite?: string;
  tiempoLimite?: number; // Añadir tiempoLimite para soportar evaluaciones con cronómetro
  preguntas: FormularioPregunta[];
}

export interface CreateRespuestaDto {
  respuestas: RespuestaItemDto[];
}

export interface RespuestaItemDto {
  preguntaId: number;
  respuestaTexto?: string;
  respuestaNumero?: number;
  opcionId?: number;
  opcionIds?: number[];
  opcionesIds?: number[];
  usuarioObjetivoId?: number;
}

export interface ValidarRespuestaDto {
  formularioId: number;
  respuestas: RespuestaItemDto[];
}

export interface FormularioMetrics {
  formulario?: {
    id: number;
    titulo: string;
  };
  general?: {
    asignacionesTotales: number;
    completados: number;
    pendientes: number;
    tasaRespuesta: number;
    tiempoPromedioHoras: number;
    tasaAbandono: number;
  };
  porRol?: Array<{
    rol: string;
    total: number;
    completados: number;
    tasaRespuesta: number;
  }>;
  porPregunta?: Array<{
    preguntaId: number;
    pregunta: string;
    tipo: string;
    totalRespuestas: number;
    media?: number;
    mediana?: number;
    distribucion?: any;
  }>;
  analisisIntentos?: Array<{
    intentoNumero: number;
    total: number;
    aprobados: number;
    mediaCalificacion: number;
  }>;
  coberturaCompetencias?: Array<{
    competencia: string;
    total: number;
    correctas: number;
    porcentaje: number;
  }>;
  discriminacionPorPregunta?: Array<{
    preguntaId: number;
    pregunta: string;
    indiceDiscriminacion: number;
  }>;
  sesgoPorRol?: Array<{
    rol: string;
    total: number;
    mediaCalificacion: number;
  }>;
}

export interface FiltrosMetricasDto {
  fechaDesde?: string;
  fechaHasta?: string;
  rol?: string;
  usuarioId?: number;
}

export interface FiltrosRespuestasDto {
  fechaDesde?: string;
  fechaHasta?: string;
  rol?: string;
  usuarioId?: number;
  preguntaId?: number;
  usuarioObjetivoId?: number;
  page?: number;
  limit?: number;
}

export interface ComparativaTemporalDto {
  periodo1Desde: string;
  periodo1Hasta: string;
  periodo2Desde: string;
  periodo2Hasta: string;
  rol?: string;
  usuarioId?: number;
}

// List formularios
export async function listFormularios(
  filters: FilterFormularioDto = {},
): Promise<{ data: Formulario[]; total: number }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Para booleanos, convertir explícitamente a 'true' o 'false'
      if (typeof value === 'boolean') {
        params.append(key, value ? 'true' : 'false');
      } else {
        params.append(key, String(value));
      }
    }
  });
  const { data } = await api.get(`${BASE_URL}?${params.toString()}`);
  return data;
}

// Get single formulario
export async function getFormulario(id: number): Promise<Formulario> {
  const { data } = await api.get(`${BASE_URL}/${id}`);
  return data;
}

// Create formulario
export async function createFormulario(payload: CreateFormularioDto): Promise<Formulario> {
  const { data } = await api.post(BASE_URL, payload);
  return data;
}

// Update formulario
export async function updateFormulario(
  id: number,
  payload: UpdateFormularioDto,
): Promise<Formulario> {
  const { data } = await api.put(`${BASE_URL}/${id}`, payload);
  return data;
}

// Delete formulario
export async function deleteFormulario(id: number): Promise<void> {
  await api.delete(`${BASE_URL}/${id}`);
}

// Duplicate formulario
export async function duplicarFormulario(id: number): Promise<Formulario> {
  const { data } = await api.post(`${BASE_URL}/${id}/duplicar`);
  return data;
}

// Activate formulario
export async function activarFormulario(id: number): Promise<Formulario> {
  const { data } = await api.patch(`${BASE_URL}/${id}/activar`);
  return data;
}

// Deactivate formulario
export async function desactivarFormulario(id: number): Promise<Formulario> {
  const { data } = await api.patch(`${BASE_URL}/${id}/desactivar`);
  return data;
}

// Extend deadline
export async function extenderPlazoFormulario(
  id: number,
  fechaLimite: string,
): Promise<Formulario> {
  const { data } = await api.patch(`${BASE_URL}/${id}/extender-plazo`, { fechaLimite });
  return data;
}

// Assign formulario
export async function asignarFormulario(id: number, payload: AsignarFormularioDto): Promise<void> {
  await api.post(`${BASE_URL}/${id}/asignar`, payload);
}

// Unassign formulario
export async function desasignarFormulario(id: number, usuarioId: number): Promise<void> {
  await api.delete(`${BASE_URL}/${id}/asignar/${usuarioId}`);
}

// Get pending formulario for user
export async function getFormularioPendiente(): Promise<FormularioPendiente | null> {
  try {
    const { data } = await api.get(`${BASE_URL}/pendiente`);
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// Submit responses
export async function submitRespuestas(
  formularioId: number,
  payload: CreateRespuestaDto,
): Promise<any> {
  const { data } = await api.post(`${BASE_URL}/${formularioId}/respuestas`, payload);
  return data;
}

// Validate responses
export async function validarRespuestas(
  formularioId: number,
  payload: ValidarRespuestaDto,
): Promise<{ valid: boolean; errors?: string[] }> {
  const { data } = await api.post(`${BASE_URL}/${formularioId}/validar`, payload);
  return data;
}

// Get metrics
export async function getFormularioMetrics(
  id: number,
  filters: FiltrosMetricasDto = {},
): Promise<FormularioMetrics> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const { data } = await api.get(`${BASE_URL}/${id}/metricas?${params.toString()}`);
  return data;
}

// Export metrics to Excel
export async function exportFormularioMetrics(
  id: number,
  filters: FiltrosMetricasDto = {},
): Promise<Blob> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const { data } = await api.get(`${BASE_URL}/${id}/exportar-excel?${params.toString()}`, {
    responseType: 'blob',
  });
  return data;
}

// Get individual responses
export async function getRespuestasIndividuales(
  id: number,
  filters: FiltrosRespuestasDto = {},
): Promise<{ data: any[]; total: number }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const { data } = await api.get(`${BASE_URL}/${id}/respuestas?${params.toString()}`);
  return data;
}

// Get assignments
export async function getAsignaciones(id: number): Promise<any[]> {
  const { data } = await api.get(`${BASE_URL}/${id}/asignaciones`);
  return data;
}

// Get temporal comparison
export async function getComparativaTemporal(
  id: number,
  filters: ComparativaTemporalDto,
): Promise<any> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const { data } = await api.get(`${BASE_URL}/${id}/comparativa-temporal?${params.toString()}`);
  return data;
}

// Get alumnos del profesor para formulario profesor_alumnos
export async function getAlumnosProfesor(formularioId: number): Promise<any[]> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/alumnos-profesor`);
  return data;
}

// Get usuarios del departamento para formulario manager_departamento
export async function getUsuariosDepartamento(formularioId: number): Promise<any[]> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/usuarios-departamento`);
  return data;
}

// Get manager del departamento para formulario evaluacion_managers
export async function getManagerDepartamento(formularioId: number): Promise<any | null> {
  try {
    const { data } = await api.get(`${BASE_URL}/${formularioId}/manager-departamento`);
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// Get usuarios objetivo predefinidos (para evaluacion_managers)
export async function getUsuariosObjetivoPredefinidos(formularioId: number): Promise<any[]> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/usuarios-objetivo-predefinidos`);
  return data;
}

// Get formularios de un usuario (donde es objetivo)
export async function getFormulariosUsuario(
  usuarioId: number,
  filtros?: { tipoFormulario?: string },
): Promise<any[]> {
  const params = new URLSearchParams();
  if (filtros?.tipoFormulario) {
    params.append('tipoFormulario', filtros.tipoFormulario);
  }
  const { data } = await api.get(`${BASE_URL}/usuario/${usuarioId}?${params.toString()}`);
  return data;
}

export async function getUsuariosObjetivoCompletados(formularioId: number): Promise<number[]> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/usuarios-objetivo-completados`);
  return data;
}

// ========== EVALUACIONES ==========

export interface AsignarEvaluacionDto {
  usuarioId: number;
  esRecurrente?: boolean;
  frecuenciaMeses?: number;
  numeroEnvios?: number;
  fechaInicio?: string;
  fechaFin?: string;
  fechaProgramada?: string;
  fechaLimitePersonalizada?: string;
}

export interface HistorialEvaluacion {
  id: number;
  titulo: string;
  descripcion?: string;
  estado: 'pendiente' | 'completado';
  asignadoAt: string;
  intentos: number;
  completados: Array<{
    intentoNumero: number;
    calificacion?: number;
    aprobado?: boolean;
    completadoAt: string;
  }>;
  ultimaCalificacion?: number;
  ultimoAprobado?: boolean;
}

export interface EvaluacionPendiente {
  usuarioId: number;
  usuarioNombre: string;
  usuarioEmail: string;
  formularioId: number;
  formularioTitulo: string;
  asignadoAt: string;
  diasPendiente: number;
  semaforo: 'verde' | 'amarillo' | 'rojo';
}

// List evaluaciones
export async function listEvaluaciones(
  filters: FilterFormularioDto = {},
): Promise<{ data: Formulario[]; total: number }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const { data } = await api.get(`${BASE_URL}/evaluaciones?${params.toString()}`);
  return data;
}

// Asignar evaluación individual
export async function asignarEvaluacionIndividual(
  formularioId: number,
  payload: AsignarEvaluacionDto,
): Promise<void> {
  await api.post(`${BASE_URL}/${formularioId}/asignar-evaluacion`, payload);
}

// Obtener historial de evaluaciones de un usuario
export async function getHistorialEvaluacionesUsuario(
  usuarioId: number,
): Promise<HistorialEvaluacion[]> {
  const { data } = await api.get(`${BASE_URL}/usuarios/${usuarioId}/evaluaciones`);
  return data;
}

// Obtener evaluaciones pendientes
export async function getEvaluacionesPendientes(): Promise<EvaluacionPendiente[]> {
  const { data } = await api.get(`${BASE_URL}/evaluaciones/pendientes`);
  return data;
}

// ========== BORRADORES ==========

export interface SaveBorradorDto {
  respuestas: RespuestaItemDto[];
  usuarioObjetivoId?: number;
}

// Guardar borrador
export async function guardarBorrador(
  formularioId: number,
  payload: SaveBorradorDto,
): Promise<void> {
  await api.post(`${BASE_URL}/${formularioId}/borrador`, payload);
}

// Obtener borrador
export async function obtenerBorrador(
  formularioId: number,
  usuarioObjetivoId?: number,
): Promise<any | null> {
  try {
    const params = new URLSearchParams();
    if (usuarioObjetivoId) {
      params.append('usuarioObjetivoId', String(usuarioObjetivoId));
    }
    const { data } = await api.get(`${BASE_URL}/${formularioId}/borrador?${params.toString()}`);
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function iniciarEvaluacion(formularioId: number): Promise<IniciarEvaluacionResponse> {
  const { data } = await api.post(`${BASE_URL}/${formularioId}/iniciar-evaluacion`);
  return data;
}

export async function verificarTiempoEvaluacion(
  formularioId: number,
): Promise<VerificarTiempoResponse> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/verificar-tiempo`);
  return data;
}

export async function listCompetencias(): Promise<Competencia[]> {
  const { data } = await api.get(`${BASE_URL}/competencias`);
  return data;
}

export async function createCompetencia(payload: {
  nombre: string;
  descripcion?: string;
  activa?: boolean;
}): Promise<Competencia> {
  const { data } = await api.post(`${BASE_URL}/competencias`, payload);
  return data;
}

export async function getPreguntasEvaluacion(
  formularioId: number,
): Promise<PreguntasEvaluacionResponse> {
  const { data } = await api.get(`${BASE_URL}/${formularioId}/preguntas-evaluacion`);
  return data;
}
